import { useMemo } from "react";
import { useAuth, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import type { LeafCondition } from "@tellescope/visibility-schema";
import {
  applyContactTypeOptions,
  buildCustomEnduserFieldDefinitions,
  getFieldsForSource,
  type FieldDefinition,
} from "./constants";

/**
 * Resolves the selectable fields for a condition source, enriching enduser fields with
 * organization-specific data: custom enduser fields and the contact type dropdown options.
 */
export const useConditionFields = (source: LeafCondition["source"]): FieldDefinition[] => {
  const { selectedOrganization } = useAuth();
  const isEnduser = source === "enduser";
  const customFields = selectedOrganization?.settings?.endusers?.customFields;

  const { data: contactTypes } = useTellescopeInfiniteQuery({
    model: "enduser_custom_types",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    enabled: isEnduser,
  });

  return useMemo(() => {
    const base = getFieldsForSource(source);
    if (!isEnduser) return base;
    // Until contact types load, the field keeps its free-text input rather than showing an empty dropdown
    const withContactTypes = contactTypes ? applyContactTypeOptions(base, contactTypes) : base;
    if (!customFields?.length) return withContactTypes;
    return [...withContactTypes, ...buildCustomEnduserFieldDefinitions(customFields)];
  }, [source, isEnduser, contactTypes, customFields]);
};
